import useGlideIndicator from './useGlideIndicator';

const ACTIVE = '[data-active-dock-tab="true"]';

/**
 * Tablet dock + phone bar selection card — the ONE card behind the active tab,
 * gliding on X through the shared engine (same feel as the desktop rail).
 *
 * - `mode` 'dock': tabs are a fixed width; bounds = [first tab left, last tab left].
 * - `mode` 'bar'  : the active chip is MORPHING wider while the old one shrinks,
 *   so the target is re-read every frame; bounds = [first left, last right - width];
 *   duration >= 240ms so card and chip finish together.
 * Active identity = the active tab's child index, so the dynamic 5th slot swapping
 * identity in place never glides. `targetTab` (a tapped-but-uncommitted tab)
 * wins over `activeTab` for the CARD only. `resetKey` (remount epochs, role, hidden keys)
 * places the card with no glide; a re-measure (badge arrival, label swap) snaps in
 * lockstep.
 */
export default function useDockIndicator(navRef, indicatorRef, { mode, activeTab, targetTab, resetKey, reducedMotion, stretch = 0, deps = [] }) {
  useGlideIndicator({
    hostRef: navRef,
    indicatorRef,
    enabled: mode === 'dock' || mode === 'bar',
    axis: 'x',
    reducedMotion,
    stretch,
    resetKey,
    minMs: mode === 'bar' ? 240 : 180,
    deps: [targetTab || activeTab, ...deps],
    observe: (nav) => Array.from(nav.querySelectorAll('[role="tab"]')),
    getTarget: (nav) => {
      // KAN-900: a pending tap moves the card at the tap, before the route
      // commits. Falls back to the active tab if the target has no button
      // (e.g. an overflow feature that only borrows the dynamic 5th slot).
      const pending = targetTab && targetTab !== activeTab
        ? nav.querySelector(`[data-dock-tab-key="${targetTab}"]`)
        : null;
      const el = pending || nav.querySelector(ACTIVE);
      if (!el) return null;
      return { id: Array.prototype.indexOf.call(nav.children, el), pos: el.offsetLeft, size: el.offsetWidth };
    },
    getBounds: (target, nav) => {
      const tabs = nav.querySelectorAll('[role="tab"]');
      if (!tabs.length) return null;
      const first = tabs[0];
      const last = tabs[tabs.length - 1];
      return mode === 'bar'
        ? [first.offsetLeft, last.offsetLeft + last.offsetWidth - target.size]
        : [first.offsetLeft, last.offsetLeft];
    },
  });
}
